import {
	readAddressMarksFromStorage,
	type StoredMarkGroups,
} from "@/lib/address-marks/storage";

export type AddressMarkMatchedField = "address" | "name" | "tag" | "group";

export interface AddressMarkSearchMatch {
	address: string;
	displayName: string;
	color?: string;
	tags: string[];
	groupNames: string[];
	groupKeys: string[];
	matchedField: AddressMarkMatchedField;
	matchedValue: string;
	matchKind: "exact" | "partial";
	score: number;
}

interface SearchableAddressMarkEntry {
	address: string;
	displayName: string;
	color?: string;
	names: string[];
	tags: string[];
	groupNames: string[];
	groupKeys: string[];
}

function normalizeSearchText(value: string) {
	return value
		.normalize("NFKC")
		.toLowerCase()
		.replace(/[“”"'`‘’]/g, "")
		.replace(/[，。！？、：；,.!?;:/\\()[\]{}<>|]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function pushUnique(target: string[], value: string | undefined) {
	const trimmed = value?.trim();
	if (!trimmed || target.includes(trimmed)) return;
	target.push(trimmed);
}

export function buildAddressMarkSearchEntries(
	markGroups: StoredMarkGroups,
): SearchableAddressMarkEntry[] {
	const addressMap = new Map<string, SearchableAddressMarkEntry>();

	markGroups.forEach((group) => {
		Object.keys(group).forEach((groupKey) => {
			const groupData = group[groupKey];
			const addresses = Array.isArray(groupData?.addresses)
				? groupData.addresses
				: [];

			addresses.forEach((item: any) => {
				const address = typeof item?.address === "string" ? item.address.trim() : "";
				if (!address) return;

				const current =
					addressMap.get(address) ||
					({
						address,
						displayName: "",
						color: undefined,
						names: [],
						tags: [],
						groupNames: [],
						groupKeys: [],
					} satisfies SearchableAddressMarkEntry);

				if (!current.displayName && typeof item?.name === "string") {
					current.displayName = item.name.trim();
				}
				if (!current.color && typeof item?.color === "string") {
					current.color = item.color;
				}

				pushUnique(current.names, item?.name);
				pushUnique(current.groupNames, groupData?.name);
				pushUnique(current.groupKeys, groupKey);

				if (Array.isArray(item?.tag)) {
					item.tag.forEach((tag: string) => pushUnique(current.tags, tag));
				}

				addressMap.set(address, current);
			});
		});
	});

	return Array.from(addressMap.values());
}

function computeEntryMatch(
	entry: SearchableAddressMarkEntry,
	query: string,
): AddressMarkSearchMatch | null {
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedQuery) return null;

	const normalizedAddress = entry.address.toLowerCase();
	if (normalizedAddress === normalizedQuery) {
		return {
			address: entry.address,
			displayName: entry.displayName || entry.address,
			color: entry.color,
			tags: entry.tags,
			groupNames: entry.groupNames,
			groupKeys: entry.groupKeys,
			matchedField: "address",
			matchedValue: entry.address,
			matchKind: "exact",
			score: 1000,
		};
	}

	let bestMatch: AddressMarkSearchMatch | null = null;

	const considerMatch = (
		values: string[],
		field: AddressMarkMatchedField,
		exactScore: number,
		containsScore: number,
	) => {
		values.forEach((value) => {
			const normalizedValue = normalizeSearchText(value);
			if (!normalizedValue) return;

			let score = -1;
			if (normalizedValue === normalizedQuery) {
				score = exactScore;
			} else if (normalizedValue.includes(normalizedQuery)) {
				score = containsScore;
			}

			if (score <= (bestMatch?.score ?? -1)) return;

			bestMatch = {
				address: entry.address,
				displayName: entry.displayName || entry.address,
				color: entry.color,
				tags: entry.tags,
				groupNames: entry.groupNames,
				groupKeys: entry.groupKeys,
				matchedField: field,
				matchedValue: value,
				matchKind: score === exactScore ? "exact" : "partial",
				score,
			};
		});
	};

	considerMatch(entry.names, "name", 920, 760);
	considerMatch(entry.groupNames, "group", 860, 700);
	considerMatch(entry.tags, "tag", 840, 680);

	if (!bestMatch && normalizedQuery.length >= 4 && normalizedAddress.includes(normalizedQuery)) {
		bestMatch = {
			address: entry.address,
			displayName: entry.displayName || entry.address,
			color: entry.color,
			tags: entry.tags,
			groupNames: entry.groupNames,
			groupKeys: entry.groupKeys,
			matchedField: "address",
			matchedValue: entry.address,
			matchKind: "partial",
			score: 620,
		};
	}

	return bestMatch;
}

export function searchAddressMarks(
	query: string,
	markGroups: StoredMarkGroups,
): AddressMarkSearchMatch[] {
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedQuery) return [];

	return buildAddressMarkSearchEntries(markGroups)
		.map((entry) => computeEntryMatch(entry, normalizedQuery))
		.filter((item): item is AddressMarkSearchMatch => Boolean(item))
		.sort((left, right) => {
			if (right.score !== left.score) {
				return right.score - left.score;
			}
			return left.address.localeCompare(right.address);
		});
}

export function searchAddressMarksInStorage(query: string) {
	return searchAddressMarks(query, readAddressMarksFromStorage());
}
